import React, { useContext } from "react";
import { InsertPhoto } from "@material-ui/icons";
import { FastField, ErrorMessage, useField, useFormikContext } from "formik";
import { Box, Grid, TextField, Typography, CircularProgress } from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { API_URLS } from "../../../../constants/api";
import { apiCall } from "../../../../utils/api";
import { ScientificProfileContext } from "../ScientificProfileContext";

const useStyles = makeStyles((theme) =>
  createStyles({
    profileImageContainer: {
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: "200px",
      textAlign: "center",
    },
    fileInputContainer: {
      border: "1px dashed #d8d7d7",
      padding: theme.spacing(1),
    },
    fileInput: {
      display: "none",
    },
    fileInputLabel: {
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    fileInputIcon: {
      fontSize: "200%",
      color: "grey",
    },
    profileImage: {
      width: "200px",
      [theme.breakpoints.down("xs")]: {
        width: "100%",
      },
    },
    sectionTitle: {
      fontWeight: "bold",
      textTransform: "uppercase",
    },
    profileImageErrorMessage: {
      color: theme.palette.error.main,
    },
  }),
);

function BasicInfo() {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const { profile } = useContext(ScientificProfileContext);
  const [base64Input, , base64Helpers] = useField("basicInfo.base64");
  const [avatarInput, , avatarHelpers] = useField("basicInfo.avatar");
  const [isUploading, setIsUploading] = React.useState(false);

  // Get the image source - prefer base64, fallback to avatar URL with domain
  const getImageSrc = () => {
    // if (base64Input.value) {
    //   return base64Input.value;
    // }
    if (avatarInput.value) {
      return `${process.env.REACT_APP_STORAGE_URI}/api/v1/${avatarInput.value}`;
    }
    return null;
  };

  const imageSrc = getImageSrc();

  /**
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  const handleSelectImage = async (event) => {
    if (event.target.files && event.target.files.length) {
      const VALID_TYPES = ["image/jpeg", "image/png"];
      const file = event.target.files[0];
      const { type } = file;

      if (VALID_TYPES.includes(type)) {
        // Show preview immediately
        const fileReader = new FileReader();
        fileReader.onloadend = (loadEndEvent) => {
          base64Helpers.setValue(loadEndEvent.target.result);
        };
        fileReader.readAsDataURL(file);

        // Upload to server
        try {
          setIsUploading(true);
          const profileId = profile?.id;
          if (!profileId) {
            return;
          }
          const formData = new FormData();
          formData.append("file", file);

          const uploadApi = API_URLS.FILE.uploadFile(profileId, "avatar");
          const { response, error } = await apiCall({
            ...uploadApi,
            payload: formData,
          });

          if (!error && response?.status === 200 && response?.data?.success) {
            const avatarUrl = response.data.data;
            avatarHelpers.setValue(avatarUrl);
          }
        } catch (error) {
          // Upload error
        } finally {
          setIsUploading(false);
        }
      }
    }
  };

  return (
    <>
      <Box pt={2}>
        <Box className={classes.profileImageContainer} py={2}>
          <Box display="flex" flexDirection="column" alignItems="center" className={classes.fileInputContainer}>
            <label className={classes.fileInputLabel}>
              {imageSrc && <img alt="Ảnh hồ sơ" src={imageSrc} className={classes.profileImage} />}
              {!imageSrc && <InsertPhoto className={classes.fileInputIcon} />}
              {isUploading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} />
                  <span>Đang tải lên...</span>
                </Box>
              ) : imageSrc ? (
                "Chọn ảnh khác"
              ) : (
                "Chọn ảnh"
              )}
              <input
                type="file"
                className={classes.fileInput}
                onChange={handleSelectImage}
                accept="image/jpeg, image/png"
                disabled={isUploading}
              />
            </label>
            <Box textAlign="center" className={classes.profileImageErrorMessage}>
              <ErrorMessage name="basicInfo.base64" />
            </Box>
          </Box>
        </Box>
        <Box>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="h6" className={classes.sectionTitle}>
                Thông tin cơ bản
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FastField name="basicInfo.name">
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      fullWidth
                      label="Tên"
                      variant="outlined"
                      size="small"
                      helperText={<ErrorMessage name="basicInfo.name" />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item md={4} sm={7} xs={6}>
              <FastField name="basicInfo.birth">
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      fullWidth
                      label="Ngày, tháng, năm sinh"
                      variant="outlined"
                      size="small"
                      type="date"
                      helperText={<ErrorMessage name="basicInfo.birth" />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item md={8} sm={5} xs={6}>
              <FastField name="basicInfo.nationality">
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      fullWidth
                      label="Quốc tịch"
                      variant="outlined"
                      size="small"
                      helperText={<ErrorMessage name="basicInfo.nationality" />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item xs={12}>
              <FastField name="basicInfo.degree">
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      fullWidth
                      label="Học hàm/học vị cao nhất"
                      variant="outlined"
                      size="small"
                      helperText={<ErrorMessage name="basicInfo.degree" />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item xs={12}>
              <FastField name="basicInfo.unit">
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      fullWidth
                      label="Đơn vị công tác"
                      variant="outlined"
                      size="small"
                      helperText={<ErrorMessage name="basicInfo.unit" />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item xs={12}>
              <FastField name="basicInfo.position">
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      fullWidth
                      label="Vai trò"
                      variant="outlined"
                      size="small"
                      helperText={<ErrorMessage name="basicInfo.position" />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item xs={12}>
              <FastField name="basicInfo.address">
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      fullWidth
                      label="Địa chỉ"
                      variant="outlined"
                      size="small"
                      helperText={<ErrorMessage name="basicInfo.address" />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item xs={6}>
              <FastField name="basicInfo.email">
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      type="email"
                      fullWidth
                      label="Email"
                      variant="outlined"
                      size="small"
                      helperText={<ErrorMessage name="basicInfo.email" />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item xs={6}>
              <FastField name="basicInfo.phone">
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      type="tel"
                      fullWidth
                      label="Số điện thoại"
                      variant="outlined"
                      size="small"
                      helperText={<ErrorMessage name="basicInfo.phone" />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
}

export default BasicInfo;

